import format from "date-fns/format";

const ADMIN_ONLY_FIELDS = [
  "top_studio",
  "cheap",
  "urgent",
  "top",
  "new",
  "deleted",
  "sold",
  "exclusive",
  "lux",
  "pictures_view",
  "contract",
  "contract_ends",
];

export const buildPropertyInitialValues = ({
  isEditMode,
  isAgent,
  currentProperty,
  differencesMapped,
  loggedAgentData,
  loggedUserAgencyType,
  findType,
}) => {
  if (!isEditMode) {
    const agencyType =
      loggedUserAgencyType || loggedAgentData?.agencies?.[0]?.type;
    const initialCategory =
      agencyType === "rental" ? "rental" : "sale";
    return {
      property_code: "",
      city: "",
      area: "",
      property_size: "",
      price: "",
      floor: "",
      rooms: "",
      type: "",
      property_sub_type: "",
      description: "",
      description_en: "",
      description_ru: "",
      category: initialCategory,
    };
  }

  const normalizeRooms = (rooms) => {
    if (rooms === null || rooms === undefined || rooms === "") return rooms;
    const num = typeof rooms === "number" ? rooms : parseFloat(rooms);
    if (isNaN(num)) return rooms;
    return num % 1 === 0 ? String(Math.floor(num)) : String(num);
  };

  const values = {
    ...currentProperty,
    ...currentProperty?.options,
    rooms: normalizeRooms(currentProperty?.rooms),
    city:
      typeof currentProperty?.city === "object"
        ? currentProperty?.city?.name
        : currentProperty?.city || "",
    agent_id: currentProperty?.agent_id || null,
    contract_ends: currentProperty?.contract_ends
      ? format(new Date(currentProperty.contract_ends), "yyyy-MM-dd")
      : null,
    property_description_publish:
      currentProperty?.options?.property_description_publish === "on",
    property_sub_type:
      currentProperty?.property_sub_type ||
      currentProperty?.options?.property_sub_type ||
      currentProperty?.type,
    property_offer_legal:
      currentProperty?.property_offer_legal === true ||
      currentProperty?.options?.property_offer_legal === true,
    pictures_view: currentProperty?.pictures_view || false,
    ...differencesMapped,
    ...differencesMapped?.options,
    description:
      differencesMapped?.description?.description !== undefined
        ? differencesMapped.description.description
        : currentProperty?.description?.description,
    description_en:
      differencesMapped?.description?.description_en !== undefined
        ? differencesMapped.description.description_en
        : currentProperty?.description?.description_en,
    description_ru:
      differencesMapped?.description?.description_ru !== undefined
        ? differencesMapped.description.description_ru
        : currentProperty?.description?.description_ru,
    type: differencesMapped?.type
      ? findType(differencesMapped.type)
      : findType(currentProperty?.type),
  };

  const resolveOptionField = (field) => {
    if (
      differencesMapped?.options &&
      Object.prototype.hasOwnProperty.call(differencesMapped.options, field)
    ) {
      return differencesMapped.options[field];
    }
    if (
      differencesMapped &&
      Object.prototype.hasOwnProperty.call(differencesMapped, field)
    ) {
      return differencesMapped[field];
    }
    return (
      currentProperty?.options?.[field] ?? currentProperty?.[field] ?? null
    );
  };

  const sanitizeDropdown = (raw) => {
    if (typeof raw === "string" && raw.startsWith("Izaberite ")) {
      return null;
    }
    return raw ?? null;
  };

  const bathroomRaw = resolveOptionField("property_offer_bathroom");
  const bathroomParsed = Number.parseInt(bathroomRaw, 10);
  values.property_offer_bathroom = Number.isInteger(bathroomParsed)
    ? bathroomParsed
    : null;

  ["property_value", "property_sub_type", "property_heat", "property_heat_type"].forEach(
    (field) => {
      values[field] = sanitizeDropdown(resolveOptionField(field));
    }
  );

  if (isAgent) {
    ADMIN_ONLY_FIELDS.forEach((field) => delete values[field]);
  }

  return values;
};

export const mapPropertyFormValuesToSubmitValues = ({
  values,
  filtersSelect,
  supportFiltersSelect,
  balconyFiltersSelect,
}) => {
  const type = values?.type;

  let submitValues = { ...values };

  submitValues = {
    ...submitValues,
    description: {
      description: values.description,
      description_en: values.description_en,
      description_ru: values.description_ru,
      approved: values.approved,
    },
    options: {
      ...submitValues.options,
      property_additional_objects: Object.keys(filtersSelect || {}).reduce(
        (acc, filt) => {
          if (filtersSelect[filt]) acc.push(filt);
          return acc;
        },
        []
      ),
      property_offer_bathroom: values.property_offer_bathroom,
      property_value: values.property_value,
      property_additional_help: Object.keys(supportFiltersSelect || {}).reduce(
        (acc, filt) => {
          if (supportFiltersSelect[filt]) acc.push(filt);
          return acc;
        },
        []
      ),
      property_tech_appliance: Object.keys(balconyFiltersSelect || {}).reduce(
        (acc, filt) => {
          if (balconyFiltersSelect[filt]) {
            if (
              (type === "top_search_cat_menu_apartments" ||
                type === "menu_office") &&
              (filt === "struja" || filt === "vodovod")
            ) {
              return acc;
            }
            acc.push(filt);
          }
          return acc;
        },
        []
      ),
      property_sub_type: values.property_sub_type,
      property_heat_type: values.property_heat_type,
      property_heat: values.property_heat,
      property_offer_legal: values.property_offer_legal,
    },
  };

  delete submitValues.property_sub_type;
  if (submitValues.property_heat_type) delete submitValues.property_heat_type;
  if (submitValues.property_heat) delete submitValues.property_heat;
  if (submitValues.property_additional_objects)
    delete submitValues.property_additional_objects;
  if (submitValues.property_additional_help)
    delete submitValues.property_additional_help;
  if (submitValues.property_tech_appliance)
    delete submitValues.property_tech_appliance;
  if (submitValues.property_value) delete submitValues.property_value;

  if (submitValues.on_floor)
    submitValues.on_floor = parseFloat(submitValues.on_floor);
  if (submitValues.total_floors)
    submitValues.total_floors = parseFloat(submitValues.total_floors);
  if (submitValues.property_size)
    submitValues.property_size = parseInt(submitValues.property_size, 10);
  if (submitValues.property_lot_size)
    submitValues.property_lot_size = parseInt(
      submitValues.property_lot_size,
      10
    );
  if (submitValues.price) submitValues.price = parseInt(submitValues.price, 10);
  if (submitValues.rooms) submitValues.rooms = parseFloat(submitValues.rooms);
  if (submitValues.property_code)
    submitValues.property_code = parseInt(submitValues.property_code, 10);

  return submitValues;
};
